/* Syba cart-state coalescer — single-flight /cart.js reads.
 *
 * WHY
 * A perf audit (2026-07-18) counted 9 separate GET /cart.js requests on one
 * PDP load: several of our own scripts plus third-party apps each fetch cart
 * state independently, often at the same instant during the load burst. This
 * module lets OUR scripts share one in-flight request instead of firing
 * duplicates. (Third-party calls are left alone.)
 *
 * WHAT THIS IS — AND DELIBERATELY IS NOT
 * Single-flight coalescing ONLY. While a GET for a given URL is in flight,
 * concurrent callers share it. The moment it settles, it is forgotten.
 * There is NO time-based cache and NO stored result, by design:
 *
 *   - A TTL cache could hand a caller pre-mutation data right after the
 *     Kaching drawer (or anything else) mutates the cart — visible wrong
 *     state, the exact class of bug we must not introduce.
 *   - Single-flight is provably never worse than the status quo: each caller
 *     receives the response of a request that was genuinely in flight at the
 *     moment it asked — indistinguishable from having fired its own fetch at
 *     that moment. The stale-around-mutation race that remains is the same
 *     race that independent fetches already have today.
 *
 * The cart-state freshness invariants in syba-cart-gift-engine.js (geo retry,
 * post-mutation re-lock, 429 fetch-gates) are untouched — that file keeps its
 * own direct fetches on purpose. Do NOT route it through this module without
 * re-reading its header comments first.
 *
 * USAGE (all consumers MUST keep a fallback so a missing module can never
 * break cart functionality — worst case is simply no dedupe):
 *
 *   var p = window.SybaCartState
 *     ? window.SybaCartState.getCart()
 *     : fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
 *
 * Each caller gets its OWN deep clone of the parsed cart, so no consumer can
 * mutate another's view of the data.
 */
(function () {
  'use strict';
  if (window.SybaCartState) return; // idempotent under double-include

  var inflight = Object.create(null); // url -> Promise<cartJson> (shared, uncloned)

  function clone(data) {
    try {
      if (typeof structuredClone === 'function') return structuredClone(data);
    } catch (e) { /* fall through to JSON clone */ }
    try { return JSON.parse(JSON.stringify(data)); } catch (e) { return data; }
  }

  function getCart(url) {
    var key = url || '/cart.js'; // exact-string key: '/cart.js' and '/de-de/cart.js' are different carts — never normalize
    if (!inflight[key]) {
      inflight[key] = fetch(key, { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(
          function (data) { delete inflight[key]; return data; },
          function (err) { delete inflight[key]; throw err; }
        );
    }
    // Clone per consumer; rejections propagate to each caller's own .catch.
    return inflight[key].then(clone);
  }

  window.SybaCartState = { getCart: getCart };
})();
